import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { debounce } from 'lodash-es';
import { Observable } from 'rxjs';

import { TemplateSelection } from 'app/apps/template-selection.model';
import { IGroupedTemplate, TemplatesTabsService } from 'app/apps/templates-tabs.service';
import { IBusinessAttribute } from 'app/business-attributes/business-attribute.model';
import { Tab } from 'app/shared/model/template-tab.model';
import { TemplateWeb } from 'app/shared/model/template.model';

import { TemplateSelectorUtils } from '../template-selector-util';

type TemplateGroup = {
  style?: string;
  name: string;
  templates: TemplateWeb[];
};

@Component({
  selector: 'cc-templates-selector-grid-view',
  templateUrl: 'templates-selector-grid-view.component.html',
  styleUrls: [
    'templates-selector-grid-view.component.scss',
    '../../../../../styles/components/template-tag-color.scss',
  ],
})
export class TemplatesSelectorGridViewComponent implements OnChanges, OnInit {
  @Input() visibleTemplates: TemplateWeb[];
  @Input() isLibrarySelection: boolean;
  @Input() tabs: Tab[];
  @Input() functions: IBusinessAttribute[];
  @Input() selectedTabIds: string[] = [];
  @Input() isSingleSelectionOnly: boolean;
  @Input() onSelectionChange: Observable<void>;

  @Output() onSelect = new EventEmitter<TemplateSelection>();
  @ViewChild('templateListContainer') templateListContainer: ElementRef;

  hideBottomBorder = true;
  groupedTemplates: TemplateGroup[] = [];
  allTemplatesSelected = false;

  debouncedUpdateBorderOnScrollEnd: () => void;

  constructor(private readonly templatesTabsService: TemplatesTabsService) {
    this.debouncedUpdateBorderOnScrollEnd = debounce(this.updateBorderOnScrollEnd, 30);
  }

  ngOnInit(): void {
    this.onSelectionChange.subscribe(() => {
      this.allTemplatesSelected = this.areAllTemplatesSelected();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.visibleTemplates) {
      this.debouncedUpdateBorderOnScrollEnd();
    }

    if (this.isLibrarySelection && this.functions?.length) {
      this.groupedTemplates = this.groupTemplatesByBusinessAttributes(this.visibleTemplates, this.functions);
    }

    if (!this.isLibrarySelection && changes.visibleTemplates && !this.selectedTabIds.length) {
      const groups = this.templatesTabsService.groupTemplatesByTabAndSort(this.visibleTemplates, this.tabs);

      this.groupedTemplates = this.convertToTemplateGroups(groups);
    }

    this.allTemplatesSelected = this.areAllTemplatesSelected();
  }

  private groupTemplatesByBusinessAttributes(templates, businessAttributes): TemplateGroup[] {
    const templatesByBusinessAttributes = templates.reduce((result, template) => {
      const templateBusinessAttributes = template.businessAttributes || [];

      templateBusinessAttributes.forEach((ba) => {
        if (!result.has(ba)) {
          result.set(ba, []);
        }

        result.get(ba).push(template);
      });

      return result;
    }, new Map());

    const filteredTemplateGroups = [];
    businessAttributes.forEach((func) => {
      const templatesForFunction = templatesByBusinessAttributes.get(func._id);

      if (templatesForFunction?.length) {
        filteredTemplateGroups.push({
          name: func.name,
          templates: templatesForFunction,
        });
      }
    });

    return filteredTemplateGroups;
  }

  private convertToTemplateGroups(groups: Map<string, IGroupedTemplate>): TemplateGroup[] {
    const templateGroups: TemplateGroup[] = [];

    groups.forEach((value) => {
      const group: TemplateGroup = {
        style: value.tab.style,
        name: value.tab.name,
        templates: value.templates,
      };

      templateGroups.push(group);
    });

    return templateGroups;
  }

  onSelectAllTemplates(event) {
    this.allTemplatesSelected = event.target.checked;
    this.onSelect.emit({ templateId: 'All', isSelected: event.target.checked });
  }

  onTemplateSelection(templateSelection: TemplateSelection) {
    this.allTemplatesSelected = this.areAllTemplatesSelected();
    this.onSelect.emit(templateSelection);
  }

  keepGroupedTemplatesInOrder() {
    return 0;
  }

  private areAllTemplatesSelected() {
    return this.visibleTemplates.every((template) => template.selected);
  }

  private updateBorderOnScrollEnd() {
    this.hideBottomBorder = !TemplateSelectorUtils.canKeepScrolling(this.templateListContainer);
  }
}
